import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { MessageCircle, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';

const BlogGalleggianteGeberit = () => {
  const handleWhatsApp = () => {
    window.open('https://wa.me/393287686356?text=Ciao,%20ho%20un%20problema%20con%20il%20galleggiante%20Geberit.%20Potete%20aiutarmi?', '_blank');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Come Sbloccare il Galleggiante Geberit | Guida Pratica SOS Casa Roma</title>
        <meta name="description" content="Guida completa per sbloccare il galleggiante Geberit cassetta incasso. Soluzioni pratiche e quando chiamare un idraulico. Tel: 328 768 6356" />
        <link rel="canonical" href={`${window.location.origin}/blog/come-sbloccare-galleggiante-geberit`} />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Come Sbloccare il Galleggiante Geberit
              </h1>
              
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  Il galleggiante della cassetta Geberit incasso può bloccarsi causando perdite d'acqua continue o mancato caricamento. Ecco come intervenire.
                </p>

                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="h-6 w-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-gray-800">
                      <strong>Attenzione:</strong> Se non ti senti sicuro o il problema persiste, chiama subito un idraulico professionista.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Sintomi di Galleggiante Bloccato
                </h2>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Acqua che scorre continuamente nel WC</li>
                  <li>Cassetta che non si riempie completamente</li>
                  <li>Rumore di acqua corrente costante</li>
                  <li>Aumento anomalo della bolletta dell'acqua</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Procedura per Sbloccare il Galleggiante Geberit
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div>
                      <p className="font-semibold text-gray-900">Chiudi l'acqua</p>
                      <p className="text-gray-700">Individua e chiudi il rubinetto di alimentazione sotto la cassetta Geberit.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div>
                      <p className="font-semibold text-gray-900">Svuota la cassetta</p>
                      <p className="text-gray-700">Aziona lo sciacquone per svuotare completamente la cassetta.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div>
                      <p className="font-semibold text-gray-900">Rimuovi la placca frontale</p>
                      <p className="text-gray-700">Sgancia la placca di comando Geberit premendo i fermi laterali.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">4</div>
                    <div>
                      <p className="font-semibold text-gray-900">Controlla il galleggiante</p>
                      <p className="text-gray-700">Verifica che il galleggiante si muova liberamente e non sia incrostato da calcare.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">5</div>
                    <div>
                      <p className="font-semibold text-gray-900">Pulisci e testa</p>
                      <p className="text-gray-700">Rimuovi eventuali incrostazioni, riapri l'acqua e verifica il funzionamento.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-gray-800">
                      <strong>Consiglio:</strong> Se il galleggiante è molto incrostato, potrebbe essere necessaria la sostituzione completa del meccanismo.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Quando Chiamare un Idraulico Professionista
                </h2>
                <p className="text-gray-700 mb-4">
                  Chiama subito SOS Casa Roma se:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Non riesci ad accedere alla cassetta incasso</li>
                  <li>Il problema persiste dopo la pulizia</li>
                  <li>Noti perdite d'acqua dal muro</li>
                  <li>Hai bisogno di sostituire il meccanismo completo</li>
                  <li>Vuoi un controllo professionale per evitare sprechi</li>
                </ul>

                <div className="bg-blue-600 rounded-lg p-8 text-center mt-12">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Problema con il Galleggiante Geberit?
                  </h3>
                  <p className="text-white text-lg mb-6">
                    Interveniamo in 30-60 minuti a Roma. Tecnici specializzati in cassette Geberit disponibili H24.
                  </p>
                  <Button
                    onClick={handleWhatsApp}
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-10 py-7 rounded-lg shadow-lg transition-all duration-300 hover:scale-105"
                  >
                    <MessageCircle className="mr-3 h-7 w-7" />
                    Chiama SOS Casa Roma su WhatsApp
                  </Button>
                  <p className="text-white mt-4 font-semibold">
                    📞 328 768 6356
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
        <FloatingWhatsApp />
      </div>
    </>
  );
};

export default BlogGalleggianteGeberit;
